/**
 * Clean up HTML strings produced by rich text editors:
 * - strips empty paragraphs / breaks at edges
 * - removes hardcoded black colors so dark mode works cleanly
 * - trims whitespace
 */
export function cleanHtml(html) {
  if (!html) return "";
  let str = html.trim();
  str = str.replace(/(?:<p[^>]*>\s*(?:<br\s*\/?>)?\s*<\/p>\s*)+$/gi, "");
  str = str.replace(/^(?:<p[^>]*>\s*(?:<br\s*\/?>)?\s*<\/p>\s*)+/gi, "");
  // Strip hardcoded black color styles so dark mode works seamlessly
  str = str.replace(/style="([^"]*)"/gi, (match, styleContent) => {
    const cleanedStyle = styleContent
      .replace(/color:\s*(?:rgb\(\s*0\s*,\s*0\s*,\s*0\s*\)|#000000|#000|black);?/gi, "")
      .trim();
    return cleanedStyle ? `style="${cleanedStyle}"` : "";
  });
  return str.trim();
}

/**
 * Determines whether a Client Wish value represents an empty state (e.g. "—", empty HTML, null)
 */
export function isEmptyWish(val) {
  if (!val) return true;
  const trimmed = val.trim();
  if (
    trimmed === "" ||
    trimmed === "—" ||
    trimmed === "-" ||
    trimmed.toLowerCase() === "(empty)" ||
    trimmed.toLowerCase() === "null" ||
    trimmed.toLowerCase() === "undefined"
  ) {
    return true;
  }
  const stripped = trimmed
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  return stripped.length === 0 && !/<img\b/i.test(trimmed);
}

/**
 * Parses Client Note activity details string into structured data:
 * - header: e.g. "Client note updated"
 * - type: note type name e.g. "Construction Notes"
 * - category: category name e.g. "Door"
 * - diffs: list of other field changes { field, old, new }
 * - clientWish: { oldWish, newWish, hasOld, hasNew, remarks }
 * - noChanges: boolean
 */
export function parseClientNoteDetails(details, activityType = "") {
  if (!details || typeof details !== "string") return null;
  const trimmed = details.trim();
  const lower = trimmed.toLowerCase();
  const typeLower = (activityType || "").toLowerCase();

  const isClientNote =
    typeLower.includes("client note") ||
    lower.startsWith("client note") ||
    trimmed.includes("Client Wish:");

  if (!isClientNote) return null;

  let header = null;
  let type = null;
  let category = null;
  let clientWish = null;
  let noChanges = false;
  const diffs = [];

  let textBeforeWish = trimmed;
  const wishIdx = trimmed.indexOf("Client Wish:");

  if (wishIdx !== -1) {
    textBeforeWish = trimmed.substring(0, wishIdx).trim();
    const wishRaw = trimmed.substring(wishIdx + "Client Wish:".length).trim();

    // Check if remarks or note are at the end of wish
    const remarksMatch = wishRaw.match(/[\r\n]+(?:Remarks|Note):\s*([\s\S]*)$/i);
    let wishBody = wishRaw;
    let remarks = null;
    if (remarksMatch) {
      remarks = remarksMatch[1].trim();
      wishBody = wishRaw.substring(0, remarksMatch.index).trim();
    }

    const arrowIdx = wishBody.indexOf(" → ");
    let oldWish = "";
    let newWish = "";
    if (arrowIdx !== -1) {
      oldWish = wishBody.substring(0, arrowIdx).trim();
      newWish = wishBody.substring(arrowIdx + 3).trim();
    } else {
      const match = wishBody.match(/^(.*?)\s*→\s*([\s\S]*)$/);
      if (match) {
        oldWish = match[1].trim();
        newWish = match[2].trim();
      } else {
        newWish = wishBody;
      }
    }

    clientWish = {
      oldWish: cleanHtml(oldWish),
      newWish: cleanHtml(newWish),
      hasOld: !isEmptyWish(oldWish),
      hasNew: !isEmptyWish(newWish),
      remarks,
    };
  }

  const lines = textBeforeWish
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (
      line.toLowerCase() === "client note updated" ||
      line.toLowerCase() === "client note added" ||
      line.toLowerCase() === "client note deleted"
    ) {
      header = line;
      continue;
    }

    if (line.toLowerCase() === "no fields changed") {
      noChanges = true;
      continue;
    }

    // Check for Type: (and not Type changed:)
    const typeMatch = line.match(/^Type:\s*(.+)$/i);
    if (typeMatch && !line.toLowerCase().startsWith("type changed")) {
      type = typeMatch[1].trim();
      continue;
    }

    // Check for Category: (and not Category changed:)
    const catMatch = line.match(/^Category:\s*(.+)$/i);
    if (catMatch && !line.toLowerCase().startsWith("category changed")) {
      category = catMatch[1].trim();
      continue;
    }

    // Check for diffs: Field: Old → New or Field changed: Old → New
    const diffMatch = line.match(/^([^:\n]+?)(?:\s+changed)?:\s*(.*?)\s*→\s*(.*)$/);
    if (diffMatch) {
      let field = diffMatch[1].trim();
      if (field.toLowerCase() === "type changed") field = "Type";
      if (field.toLowerCase() === "category changed") field = "Category";
      diffs.push({
        field,
        old: diffMatch[2].trim(),
        new: diffMatch[3].trim(),
      });
      continue;
    }
  }

  return {
    isClientNote: true,
    header,
    type,
    category,
    diffs,
    clientWish,
    noChanges,
  };
}
